import { ComponentType, ReactNode, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { setDashboardDetails } from '@/store/slices/dashboard/dashboardDetailsSlice';
import {
    setWatchAccordionOpenSection,
    type WatchAccordionSectionId,
} from '@/store/slices/dashboard/watchAccordionSlice';
import { RootState } from '@/store/index';
import { getDashboardDetailsApiCall } from '@/services/api';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import CourseCard from '@/components/dashboard/courseCard'
import Evaluate from '@/pages/dashboard/sections/evaluate'
import {
    Award,
    Bell,
    BookOpen,
    ChevronRightIcon,
    FileText,
    GraduationCap,
    Library,
    Link,
    Lock,
    LockKeyholeIcon,
} from "lucide-react";

import WhiteLock from '@/assets/icons/whiteLock.png'
import BlackLock from '@/assets/icons/blackLock.png'

import './watch.css';
import { handleUpdateUserPurchaseContentApiCall, handleUpdateWelcomeSeriesContentApiCall } from '@/utils/apiUtils';
import { addCurrentThemeClassToEle } from '@/utils';
import { pickFirstVideoDocId } from '@/utils/pickFirstVideoDocId';
import { themes } from '@/constants/colors_v2';
import { useLocation, useNavigate } from 'react-router-dom';
import { isStudentNoticeUnread } from '@/pages/notification/data';
import { courseStatus, seriesStatus } from '@/pages/dashboard/constants';
import Tag from '@/components/common/tag/tag';
import { WatchSectionNotFound } from '@/components/common/watchSectionNotFound';
import RedActionReq from '@/assets/icons/redActionReq.png';

/** Matches `CourseCard` v1 `actionRequired` logic so the accordion label stays in sync. */
function courseShowsActionRequired(course: any): boolean {
    if (!course) return false;
    if (course.courseStatus === courseStatus.completed && course.courseCompletedAt) {
        return false;
    }
    return Boolean(
        course.isVerificationIncomplete ||
            (course.isBSSFormRequired && course.isBssIncomplete) ||
            course.installmentOverdue
    );
}

/** Aligns with `SeriesCard` lock overlay (non-navigable when locked). */
function isSerieRowLocked(serie: any, anyOneInstallmentOverDue: boolean): boolean {
    if (!serie) return true;
    if (anyOneInstallmentOverDue) return true;
    if (
        serie.seriesStatus === seriesStatus.paused ||
        serie.seriesStatus === seriesStatus.enrolled
    ) {
        return true;
    }
    if (
        serie.seriesStatus === seriesStatus.expired &&
        serie.createdAt &&
        serie.validityDays
    ) {
        return true;
    }
    return false;
}

type AccordionTheme = {
    light: {
        background: string;
        borderColor: string;
        borderTopColor: string;
        iconColor: string;
        /** Solid color or CSS `linear-gradient(...)` — applied as `background`. */
        iconBgColor: string;
    };
    dark: {
        background: string;
        borderColor: string;
        borderTopColor: string;
        iconColor: string;
        iconBgColor: string;
    };
};

type WatchAccordionIconProps = {
    size?: number | string;
    className?: string;
    strokeWidth?: number;
};

type WatchAccordionSectionProps = {
    /** Redux key: only this section may be open; restored when returning to Watch. */
    sectionId: WatchAccordionSectionId;
    /** Primary heading label (plain text). */
    title: string;
    /** Optional content shown after `title` on the same row (e.g. unread badge). */
    postTitle?: ReactNode;
    /** Optional secondary line below the title row. */
    subtitle?: string;
    /** Lucide-style icon component (e.g. `Bell`). */
    icon: ComponentType<WatchAccordionIconProps>;
    /** Passed to the icon as `size` (default 20). */
    iconSize?: number;
    theme: AccordionTheme;
    /** Optional id on the section root (e.g. for scroll targets). */
    rootId?: string;
    children: ReactNode;
};

function WatchAccordionSection({
    sectionId,
    title,
    postTitle,
    subtitle,
    icon: Icon,
    iconSize = 20,
    theme,
    rootId,
    children
}: WatchAccordionSectionProps) {
    const currentTheme = useSelector((state: RootState) => state.currentThemeReducer.currentTheme);
    const openSectionId = useSelector(
        (state: RootState) => state.watchAccordionReducer.openSectionId
    );
    const dispatch = useDispatch();
    const isOpen = openSectionId === sectionId;
    const activeTheme = currentTheme === themes.dark ? theme.dark : theme.light;

    const handleHeaderClick = () => {
        dispatch(
            setWatchAccordionOpenSection(isOpen ? null : sectionId)
        );
    };

    return (
        <div
            id={rootId}
            className="watchAccordionSection"
            style={{
                background: activeTheme.background,
                borderColor: activeTheme.borderColor,
                borderTopColor: activeTheme.borderTopColor
            }}
        >
            <button
                type="button"
                className="watchAccordionHeader"
                style={{
                    borderBottom: `1px solid ${isOpen ? activeTheme.borderColor : "transparent"}`
                }}
                onClick={handleHeaderClick}
            >
                <div className="watchAccordionHeaderLeft">
                    <span
                        className="watchAccordionIconWrap"
                        style={{
                            background: activeTheme.iconBgColor,
                            color: activeTheme.iconColor
                        }}
                    >
                        <Icon size={iconSize} strokeWidth={2} aria-hidden />
                    </span>
                    <h2 className="watchAccordionTitle">
                        <span className="watchAccordionTitleRow">
                            <span className="watchAccordionTitleText">{title}</span>
                            {postTitle != null && postTitle !== false && postTitle !== "" ? postTitle : null}
                        </span>
                        {subtitle ? (
                            <span className="watchAccordionSubtitle">{subtitle}</span>
                        ) : null}
                    </h2>
                </div>
                <span className="watchAccordionToggleIcon">{isOpen ? "−" : "+"}</span>
            </button>
            <div className={`watchAccordionContent ${isOpen ? "open" : ""}`}>
                <div className="watchAccordionContentInner">{children}</div>
            </div>
        </div>
    );
}

function WatchAccordionSkeleton() {
    return (
        <div className="watchAccordionList watchAccordionList_skeleton pb-[130px] min-[850px]:pb-[0px] animate-pulse">
            {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="watchAccordionSkeletonSection">
                    <div className="watchAccordionSkeletonHeader">
                        <div className="watchAccordionSkeletonIcon" />
                        <div className="watchAccordionSkeletonTitle" />
                        <div className="watchAccordionSkeletonToggle" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function Watch(props: any) {
    const { dashboard, common } = useLocalTranslation();
    const watchEmpty = dashboard.watchSection.empty;
    const navigate = useNavigate();
    const location = useLocation();
    const timelineDetails = useSelector((state: RootState) => state.timelineReducer.timeline);

    const dispatch = useDispatch();
    const dashboardDetails = useSelector((state: RootState) => state.dashboardDetailsReducer.dashboardDetails);
    const currentTheme = useSelector((state: RootState) => state.currentThemeReducer.currentTheme);
    const [isLoading, setIsLoading] = useState(false);
    const [anyOneInstallmentOverDue, setAnyOneInstallmentOverDue] = useState(false)
    const studentNotices = dashboardDetails?.studentNotices ?? [];
    const unreadNoticeCount = useMemo(
        () => studentNotices.filter((n: any) => isStudentNoticeUnread(n)).length,
        [studentNotices]
    );
    const subscriptionGroups = useMemo(() => {
        const raw = dashboardDetails?.subscriptionGroups ?? [];
        if (!Array.isArray(raw)) return [];
        return [...raw].sort((a: any, b: any) => (a.priority ?? 0) - (b.priority ?? 0));
    }, [dashboardDetails?.subscriptionGroups]);
    const studyResources = dashboardDetails?.studyResources ?? [];
    const courses = dashboardDetails?.courses;
    const hasPurchasedCourses = Array.isArray(courses) && courses.length > 0;
    const anyCourseActionRequired = useMemo(() => {
        if (!Array.isArray(courses) || courses.length === 0) return false;
        return courses.some((c: any) => courseShowsActionRequired(c));
    }, [courses]);

    const anySeriesActionRequired = useMemo(() => {
        const list = dashboardDetails?.series;
        if (!Array.isArray(list) || list.length === 0) return false;
        return list.some((s: any) => isSerieRowLocked(s, anyOneInstallmentOverDue));
    }, [dashboardDetails?.series, anyOneInstallmentOverDue]);

    useEffect(() => {
        if (props.activeKey && props.activeKey === "watch") {
            // if (Object.entries(dashboardDetails).length === 0) {
            //     getDashboardDetails();
            // } else {
            //     handleCourseAction(dashboardDetails)
            //     setIsLoading(false)
            // }
            getDashboardDetails();
        }
    }, [props.activeKey]);


    const getDashboardDetails = async () => {
        setIsLoading(true);
        try {
            const paylaod = {
                "purchaseId": null
            }
            const handleUpdateUserPurchaseContentApiCallStatus = await handleUpdateUserPurchaseContentApiCall(paylaod)
            const handleUpdateWelcomeSeriesContentApiCallStatus = await handleUpdateWelcomeSeriesContentApiCall(paylaod)
            if (handleUpdateUserPurchaseContentApiCallStatus && handleUpdateWelcomeSeriesContentApiCallStatus) {
                const dashboardDetails = await getDashboardDetailsApiCall();
                if (dashboardDetails.data && dashboardDetails.status) {
                    dispatch(setDashboardDetails(dashboardDetails.data));
                    handleCourseAction(dashboardDetails.data);
                }
            }
        } finally {
            setIsLoading(false);
        }
    };


    const handleCourseAction = (watchDetails: any) => {
        if (watchDetails.courses && watchDetails.courses.length > 0) {
            let tempOverdueCourseIndexes: number[] = [];

            watchDetails.courses.forEach((course: any, index: number) => {
                if (course.installmentOverdue) {
                    tempOverdueCourseIndexes.push(index);
                }
            });

            // console.log(tempOverdueCourseIndexes)
            if (tempOverdueCourseIndexes.length > 0) {
                setAnyOneInstallmentOverDue(true)
            }
        }
    };

    const handleSerieNavigate = (serie: any, fromSubscription?: boolean) => {
        if (isSerieRowLocked(serie, anyOneInstallmentOverDue)) return;
        const videoDocId = pickFirstVideoDocId(serie);
        if (!serie?.seriesId) {
            alert("Issue in finding series id and video id");
            return;
        }
        let path = `/watch?series=${serie.seriesId}`;
        if (videoDocId) {
            path += `&video=${videoDocId}`;
        }
        if (serie.isWelcomeSeries) {
            path += "&ws=true";
        }
        if (fromSubscription) {
            path += "&subscription=true";
        }
        const source = location.pathname.split("/")[1];
        navigate(path, {
            state: {
                source: `/${source}`,
                purchaseId: timelineDetails?.purchaseId,
            },
        });
    };

    return (
        <div className={`watchViewMain ${addCurrentThemeClassToEle(currentTheme, "watchViewMain")}`}>
            {isLoading ? (
                <WatchAccordionSkeleton />
            ) : (
            <div className="watchAccordionList pb-[130px] min-[850px]:pb-[0px]">
                    <WatchAccordionSection
                        sectionId="notices"
                        title="Notice(s)"
                        postTitle={
                            unreadNoticeCount > 0 ? <span className="watchAccordionTitleBadge">{unreadNoticeCount}</span> : null   
                        }
                        icon={Bell}
                        theme={{
                            light: {
                                background: "linear-gradient(135deg, #F9FAFB 0%, #EFF6FF 100%)",
                                borderColor: "#B8D0FF",
                                borderTopColor: "#B8D0FF",
                                iconColor: "#4E7AB9",
                                iconBgColor: "linear-gradient(135deg, #EBF5FF 0%, #DDEAFA 100%)"
                            },
                            dark: {
                                background: "#112647",
                                borderColor: "#2E4DAB4D",
                                borderTopColor: "#2E4DAB4D",
                                iconColor: "#51A2FF",
                                iconBgColor: "#3B82F626"
                            }
                        }}
                    >
                        {studentNotices.length > 0 ? (
                            <div className="watchNoticeInner">
                                <div className="watchNoticeList">
                                    {studentNotices.map((n: any) => (
                                        <button
                                            key={n._id}
                                            type="button"
                                            className="watchNoticeRow"
                                            onClick={() => navigate(`/notification/${n._id}`)}
                                        >
                                            <div className="watchNoticeRowLeft">
                                                {isStudentNoticeUnread(n) && (
                                                    <span className="watchNoticeDot" />
                                                )}
                                                <span className="watchNoticeTitle">{n.title}</span>
                                            </div>
                                            <ChevronRightIcon size={20} className="watchNoticeChevron" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <WatchSectionNotFound
                                icon={Bell}
                                title={watchEmpty.noticesTitle}
                                subtitle={
                                    watchEmpty.noticesSubtitle?.trim()
                                        ? watchEmpty.noticesSubtitle
                                        : undefined
                                }
                            />
                        )}
                    </WatchAccordionSection>

                    <WatchAccordionSection
                        sectionId="subscription"
                        rootId="watch-accordion-subscription"
                        title="Subscription"
                        icon={Award}
                        theme={{
                            light: {
                                background: "linear-gradient(90deg, rgba(250,245,255,1) 0%, rgba(248,250,252,1) 100%)",
                                borderColor: "#E9D4FF",
                                borderTopColor: "#E9D5FF",
                                iconColor: "#AD46FF",
                                iconBgColor: "linear-gradient(135deg, #F0E0FF 0%, #FFD9EE 100%)"
                            },
                            dark: {
                                background: "#861043B2",
                                borderColor: "#A3004C",
                                borderTopColor: "#A3004C",
                                iconColor: "#FDA5D5",
                                iconBgColor: "#A3004C"
                            }
                        }}
                    >
                        {subscriptionGroups.length > 0 ? (
                            <div className="watchNoticeInner">
                                <div className="watchSubscriptionList">
                                    {subscriptionGroups.map((g: any) => (
                                        <button
                                            key={g._id}
                                            type="button"
                                            className="watchNoticeRow"
                                            onClick={() =>
                                                navigate(`/subscriptionGroup/${encodeURIComponent(g._id)}`)
                                            }
                                        >
                                            <div className="watchNoticeRowLeft">
                                                <span className="watchNoticeTitle">{g.title}</span>
                                            </div>
                                            <ChevronRightIcon size={20} className="watchSubscriptionChevron" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <WatchSectionNotFound
                                icon={Award}
                                title={watchEmpty.subscriptionTitle}
                                subtitle={
                                    watchEmpty.subscriptionSubtitle?.trim()
                                        ? watchEmpty.subscriptionSubtitle
                                        : undefined
                                }
                                button={watchEmpty.subscriptionCta}
                                onClick={()=>{
                                    if (window.parent !== window) {
                                        window.parent.postMessage(
                                          { type: 'emediskill-dashboard-open-subscription-flow' },
                                          process.env.REACT_APP_USER_PANEL_URL|| '*'
                                        );
                                      }
                                }}
                            />
                        )}
                    </WatchAccordionSection>

                    {hasPurchasedCourses && (
                    <WatchAccordionSection
                        sectionId="courses"
                        rootId="watch-accordion-courses"
                        title={dashboard.watchSection.courses.title}
                        postTitle={
                            anyCourseActionRequired ? (
                                <span className="watchAccordionActionRequiredPill">
                                    <Tag
                                        text={common.status.actionRequired}
                                        icon={RedActionReq}
                                        colorCode="red"
                                        borderR_tr="5px"
                                        borderR_br="5px"
                                        borderR_tl="5px"
                                        borderR_bl="5px"
                                        iconWidth="10px"
                                        textSize="13px"
                                    />
                                </span>
                            ) : undefined
                        }
                        icon={BookOpen}
                        theme={{
                            light: {
                                background: "linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)",
                                borderColor: "#B9F8CF",
                                borderTopColor: "#B9F8CF",
                                iconColor: "#15803d",
                                iconBgColor: "linear-gradient(135deg, #B2FFC9 0%, #CDFFE8 100%)",
                            },
                            dark: {
                                background: "#004F3B",
                                borderColor: "#006045",
                                borderTopColor: "#006045",
                                iconColor: "#5EE9B5",
                                iconBgColor: "#006045"
                            }
                        }}
                    >
                        {dashboardDetails.courses && dashboardDetails.courses.length > 0 ? (
                            <div className={`courseMain`}>
                                <div className="courseMain1 rounded-[7px] grid max-[600px]:grid-cols-2 max-[1200px]:grid-cols-3 grid-cols-5">
                                    {dashboardDetails.courses.map((course: any, index: number) => (
                                        <CourseCard
                                            key={index}
                                            course={course}
                                            anyOneInstallmentOverDue={anyOneInstallmentOverDue}
                                            allCourses={dashboardDetails.courses}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </WatchAccordionSection>
                    )}

                    {hasPurchasedCourses && (
                    <WatchAccordionSection
                        sectionId="series"
                        title={dashboard.watchSection.series.title}
                        postTitle={
                            anySeriesActionRequired ? (
                                <span className="watchAccordionActionRequiredPill">
                                    <Tag
                                        text={common.status.actionRequired}
                                        icon={RedActionReq}
                                        colorCode="red"
                                        borderR_tr="5px"
                                        borderR_br="5px"
                                        borderR_tl="5px"
                                        borderR_bl="5px"
                                        iconWidth="10px"
                                        textSize="13px"
                                    />
                                </span>
                            ) : undefined
                        }
                        icon={Library}
                        theme={{
                            light: {
                                background: "linear-gradient(135deg, #FFF7ED 0%, #FEF2F2 100%)",
                                borderColor: "#FFD6A7",
                                borderTopColor: "#FEE2C5",
                                iconColor: "#FF6900",
                                iconBgColor: "linear-gradient(135deg, #FFE8CB 0%, #FBD7D7 100%)"
                            },
                            dark: {
                                background: "#7B3306B2",
                                borderColor: "#973C00",
                                borderTopColor: "#973C00",
                                iconColor: "#FFD230",
                                iconBgColor: "#973C00"
                            }
                        }}
                    >
                        {anySeriesActionRequired &&
                            dashboardDetails.series &&
                            dashboardDetails.series.length > 0 && (
                                <div
                                    className={`watchSeriesLockBanner ${addCurrentThemeClassToEle(
                                        currentTheme,
                                        "watchSeriesLockBanner"
                                    )}`}
                                >
                                    <LockKeyholeIcon
                                        className="watchSeriesLockBannerIcon"
                                        size={12}
                                        strokeWidth={2}
                                        aria-hidden
                                    />
                                    <p className="watchSeriesLockBannerText">
                                        {dashboard.watchSection.series.timelineUnlockBanner}
                                    </p>
                                </div>
                            )}
                        {dashboardDetails.series && dashboardDetails.series.length > 0 ? (
                            <div className="watchNoticeInner">
                                <div className="watchSubscriptionList">
                                    {dashboardDetails.series.map((serie: any, index: number) => {
                                        const locked = isSerieRowLocked(serie, anyOneInstallmentOverDue);
                                        return (
                                            <button
                                                key={serie._id ?? index}
                                                type="button"
                                                className="watchNoticeRow"
                                                disabled={locked}
                                                onClick={() => handleSerieNavigate(serie)}
                                            >
                                                <div className="watchNoticeRowLeft">
                                                    <span className="watchNoticeTitle watchSeriesRowTitleCell">
                                                        {serie.title}
                                                    </span>
                                                    
                                                </div>
                                                {locked ? (
                                                    <img
                                                        src={
                                                            currentTheme === themes.dark
                                                                ? WhiteLock
                                                                : BlackLock
                                                        }
                                                        alt=""
                                                        className="watchSeriesLockIcon"
                                                        width={20}
                                                        height={20}
                                                        draggable={false}
                                                    />
                                                ) : (
                                                    <ChevronRightIcon size={20} className="watchSeriesChevron" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                    </WatchAccordionSection>
                    )}

                    {hasPurchasedCourses && (
                    <WatchAccordionSection
                        sectionId="evaluation"
                        title="Evaluation"
                        icon={GraduationCap}
                        theme={{
                            light: {
                                background: "linear-gradient(135deg, #EEF2FF 0%, #FAF5FF 100%)",
                                borderColor: "#C6D2FF",
                                borderTopColor: "#C6D2FF",
                                iconColor: "#556BE5",
                                iconBgColor: "linear-gradient(135deg, #EAEFFF 0%, #ECDAFF 100%)"
                            },
                            dark: {
                                background: "#4D179AB2",
                                borderColor: "#5D0EC0",
                                borderTopColor: "#5D0EC0",
                                iconColor: "#C4B4FF",
                                iconBgColor: "#5D0EC0"
                            }
                        }}
                    >
                        <Evaluate activeKey={props.activeKey} forceLoad={true} embedInWatch />
                    </WatchAccordionSection>
                    )}

                    <WatchAccordionSection
                        sectionId="studyResources"
                        title="Study Resources"
                        icon={FileText}
                        theme={{
                            light: {
                                background: "linear-gradient(135deg, #F9F9F9 0%, #F1F2F0 99.59%)",
                                borderColor: "#E5E7EB",
                                borderTopColor: "#E5E7EB",
                                iconColor: "#4A5565",
                                iconBgColor: "linear-gradient(142.07deg, #F3F4F6 6.08%, #8E8F90 403.78%)"
                            },
                            dark: {
                                background: "#1D293DB2",
                                borderColor: "#314158",
                                borderTopColor: "#314158",
                                iconColor: "#CAD5E2",
                                iconBgColor: "#314158"
                            }
                        }}
                    >
                        {studyResources.length > 0 ? (
                            <div className="watchStudyResourcesGrid">
                                {studyResources.map((r: any) => (
                                    <button
                                        key={r._id}
                                        type="button"
                                        className="watchStudyResourceCard"
                                        onClick={() => navigate(`/resources/${r._id}`)}
                                    >
                                        <span className="watchStudyResourceCardTitle">{r.title}</span>
                                        <span className="watchStudyResourceCardIcon" aria-hidden="true">
                                            <Link size={16} />
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <WatchSectionNotFound
                                icon={FileText}
                                title={watchEmpty.studyResourcesTitle}
                                subtitle={
                                    watchEmpty.studyResourcesSubtitle?.trim()
                                        ? watchEmpty.studyResourcesSubtitle
                                        : undefined
                                }
                            />
                        )}
                    </WatchAccordionSection>
            </div>
            )}
        </div>
    );
}
